import { ChangeDetectionStrategy, Component, Inject, inject, input, model, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FikoYTKokBilesenComponent } from '@/src/service/kok-bilesen/kok-bilesen.component';
import { RouterModule } from '@angular/router';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzWaveModule } from 'ng-zorro-antd/core/wave';
import { NZ_MODAL_DATA } from 'ng-zorro-antd/modal';
import { FikoYTIstekKok } from '@/src/service/modules/fikoyt-istek-kok/fikoyt.istek.makinesi.service';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { CanliGuncellemeService } from '../../../dinamik-bilesen/canli-guncelleme.service';
import { KartBildirimService } from '../../../dinamik-bilesen/kart-bildirim.service';

declare var $: any;

@Component({
  templateUrl: './.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, RouterModule, NzIconModule, NzButtonModule, NzWaveModule, NzPopconfirmModule, NzDatePickerModule],
})
export class KartMenuComponent extends FikoYTKokBilesenComponent implements OnDestroy {
  /** 📢 premium özellik lisansı (köprü yoksa AÇIK). */
  lisans(alt: string): boolean { try { return (window as any).__fikoLisans?.('📢', alt) ?? true; } catch { return true; } }

  kart = model(null);
  hatirlatmaTarihi: Date | null = null;
  hatirlatmaPaneliAcik = false;
  private canliGuncelleme = inject(CanliGuncellemeService);
  private kartBildirim = inject(KartBildirimService);

  constructor(@Inject(NZ_MODAL_DATA) public data: any,) {
    super();
  }

  override ngAfterViewInit(): void {
    this.kart.set(structuredClone(this.data?.kart ?? null));
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  tiklandiKartDuzenle() {
    this.nzModalRef.close({ aksiyon: 'duzenle', kart: this.kart() });
  }

  async tiklandiKartKopyala() {
    const kopya = structuredClone(this.kart());
    if (kopya) {
      delete kopya._id;
      kopya.tarih = new Date();
      this.nzModalRef.close({ aksiyon: 'kopyala', kart: kopya });
    }
  }

  tiklandiSablonKaydet() {
    this.nzModalRef.close({ aksiyon: 'sablonKaydet', kart: this.kart() });
  }

  async tiklandiSabitle() {
    const kart = this.kart();
    if (!kart?._id) return;

    const yeniDeger = !kart.sabitlendi;
    try {
      const istek = await FikoYTIstekKok.request({
        metod: 'POST',
        komut: 'FikoYT.sistem.DB.hareketler.guncelle',
        parametreler: {
          filter: { _id: kart._id },
          set: { sabitlendi: yeniDeger }
        },
        tekrarSayisi: 3,
        oncelik: 5,
        gecikmeliCevap: false,
      });
      const cevap: any = await istek.isteSync();
      if (cevap?.cevap?.sonuc) {
        kart.sabitlendi = yeniDeger;
        this.kart.set({ ...kart });
        this.nzMessage.success(yeniDeger ? 'Kart sabitlendi' : 'Sabitleme kaldırıldı');
      } else {
        this.nzMessage.error('İşlem başarısız');
      }
    } catch (e) {
      this.nzMessage.error('Sabitleme işlemi sırasında hata oluştu');
    }
    this.nzModalRef.close();
  }

  tiklandiKoleksiyonaEkle() {
    this.nzModalRef.close({ aksiyon: 'koleksiyonaEkle', kart: this.kart() });
  }

  tiklandiLinkKopyala() {
    const kart = this.kart();
    if (kart?._id) {
      const link = `${window.location.origin}/kart/${kart._id}`;
      navigator.clipboard.writeText(link).then(() => {
        this.nzMessage.success('Link panoya kopyalandı');
      }).catch(() => {
        this.nzMessage.error('Link kopyalanamadı');
      });
    }
    this.nzModalRef.close();
  }

  async tiklandiKartSil() {
    try {
      const kartId = this.kart()?._id;
      if (!kartId) return;

      // Tüm bağlı kartları topla: ana kart + alt kartlar + tüm içerikler (recursive)
      const tumIdler = await this.altKartIdleriniTopla(kartId);

      const istek = await FikoYTIstekKok.request({
        metod: 'POST',
        komut: 'FikoYT.sistem.DB.hareketler.guncelle',
        parametreler: {
          filter: { _id: { $in: tumIdler } },
          set: { silindi: true }
        },
        tekrarSayisi: 3,
        oncelik: 5,
        gecikmeliCevap: false,
      });
      let cevap: any = await istek.isteSync();

      if (cevap.cevap.sonuc) {
        const silinecekKart = this.kart();
        if (silinecekKart?._id) {
          this.canliGuncelleme.silindiYayin(silinecekKart._id);
          this.kartBildirim.bildirimTetikle('kart_silindi', silinecekKart._id, silinecekKart.sahip, silinecekKart.veri?.kurdela?.baslik);
        }
        this.CD.detectChanges();
        this.nzMessage.success('Kart silindi');
      } else {
        this.nzMessage.error('Kart silinemedi');
      }
    } catch (e) {
      this.nzMessage.error('Silme sırasında bir hata oluştu');
    }
  }

  /** Verilen kart ID'sinden başlayarak rekürsif olarak tüm alt kartları + içeriklerini topla */
  private async altKartIdleriniTopla(kartId: string): Promise<string[]> {
    const tumIdler = new Set<string>();
    const kuyruk: string[] = [kartId];

    while (kuyruk.length > 0) {
      const isle = kuyruk.splice(0, kuyruk.length);
      isle.forEach(id => tumIdler.add(id));

      try {
        const istek = await FikoYTIstekKok.request({
          metod: 'POST',
          komut: 'FikoYT.sistem.DB.hareketler.aggregate',
          parametreler: {
            aggregate: [
              { $match: { _uid: { $in: isle }, silindi: { $ne: true } } },
              { $project: { _id: 1, 'veri.icerikler': 1 } }
            ]
          },
          oncelik: 5,
          gecikmeliCevap: false,
        });
        const cevap: any = await istek.isteSync();
        const altlar = cevap?.cevap?.sonuc || [];
        for (const a of altlar) {
          if (a._id && !tumIdler.has(a._id)) kuyruk.push(a._id);
          // İçerik ID'lerini de ekle
          const icIdler = a.veri?.icerikler || [];
          for (const ic of icIdler) {
            if (typeof ic === 'string' && !tumIdler.has(ic)) tumIdler.add(ic);
          }
        }
      } catch {}
    }
    return Array.from(tumIdler);
  }

  tiklandiHatirlatma() {
    this.hatirlatmaPaneliAcik = !this.hatirlatmaPaneliAcik;
    this.CD.detectChanges();
  }

  async hatirlatmaKaydet() {
    if (!this.hatirlatmaTarihi) {
      this.nzMessage.warning('Tarih seçiniz');
      return;
    }
    const kart = this.kart();
    if (!kart?._id) return;
    try {
      const { BildirimService } = await import('@/src/app/servisler/bildirim.service');
      const bildirimSvc = this.injector.get(BildirimService);
      await bildirimSvc.hatirlatmaOlustur(
        kart._id,
        kart.veri?.kurdela?.baslik || '',
        this.hatirlatmaTarihi.toISOString(),
      );
      this.nzMessage.success('Hatırlatma ayarlandı');
      this.hatirlatmaPaneliAcik = false;
      this.hatirlatmaTarihi = null;
      this.nzModalRef.close();
    } catch {
      this.nzMessage.error('Hatırlatma kaydedilemedi');
    }
  }
}
